<template>
	<div class="residential-home">
		<ResidentialHomeNavbar @openCart="openCartSummary" />
		<ResidentialComingSoonSection v-if="showComingSoon" />
		<div class="home-container desktop-center" v-else>
			<ResidentialHomeBanner @toggleLoader="toggleLoader" />
			<div class="home-row">
				<div class="home-section" v-if="menus.length > 0">
					<h2>
						<ResidentialDaySelector />
					</h2>
					<div class="residential-menus">
						<router-link
							v-for="menu in menus"
							:key="menu.id"
							tag="div"
							:to="`/${slug}/menu?scroll_to_menu=${menu.id}`"
							@click.native="viewMenuClicked(menu)"
						>
							<div
								class="residential-menu-image"
								:style="{
									backgroundImage: `url(${
										menu.itemImage
											? menu.itemImage
											: `/images/home-menu-placeholder.jpg`
									})`
								}"
							>
								<div
									class="pill pill-dark-navy"
									v-if="menu.nowServing"
								>
									Now Serving
								</div>
							</div>
							<h2 class="ellipsify-text">
								{{ menu.name }}
							</h2>
							<div class="residential-menu-times ellipsify-text">
								{{ menu.timeRange }}
							</div>
						</router-link>
					</div>
					<router-link
						class="home-section-link"
						:to="`/${slug}/menu`"
						@click.native="viewActiveMenuClicked"
					>
						Explore Active Menu
						<i class="sk-icon-chevron-right"></i>
					</router-link>
				</div>
				<div class="home-section">
					<h2>Exclusive Events</h2>
					<router-link
						v-if="event"
						class="event-details"
						tag="div"
						:to="`/${slug}/menu/event/${event.id}`"
						@click.native="eventClicked"
					>
						<div>
							<div
								class="upcoming-event-image"
								:style="eventBannerStyle"
							>
								<div
									v-if="isInPreorderStatus"
									class="pill pill-dark-navy last-call-pill"
								>
									Last Call
									<b>{{ lastCall }}</b>
								</div>
								<div
									v-else
									class="pill pill-dark-navy last-call-pill"
								>
									Ongoing
								</div>
							</div>
							<div class="upcoming-event-details">
								<div class="event-date-pill">
									<div>{{ eventMonth }}</div>
									<b>{{ eventDay }}</b>
								</div>
								<div>
									<h2>{{ event.name }}</h2>
									<div class="event-time">
										{{ eventTime }}
									</div>
								</div>
							</div>
						</div>
						<div
							class="residential-menus"
							v-if="nextEvents.length > 0 && menus.length == 0"
						>
							<div class="event-cards">
								<EventCard
									v-for="event in nextEvents"
									:key="event.id"
									:event="event"
									:isUpcoming="false"
								/>
							</div>
						</div>

						<router-link
							@click.native="viewEventsClicked"
							class="home-section-link"
							:to="`/${slug}/events`"
						>
							All Upcoming Events
							<i class="sk-icon-chevron-right"></i>
						</router-link>
					</router-link>
					<div v-else class="event-placeholder">
						<div>
							There are no upcoming events in
							{{ businessName }}
						</div>
						<slot v-if="showEventNotifyButton">
							<div>
								Get a convenient notification the next time an
								event is scheduled!
							</div>
							<button
								@click="eventReminderClicked"
								class="button button-secondary-dark button-auto"
							>
								<i class="sk-icon-bell-on-solid"></i>
								Notify Me
							</button>
						</slot>
					</div>
				</div>
			</div>
			<div class="home-row" v-if="showPerks">
				<div class="residential-loyalty">
					<h2>Enjoying 2ndKitchen</h2>
					<div class="subtitle">
						Maximize your experience with these awesome perks!
					</div>
					<div class="residential-loyalty-perks">
						<LoyaltyWidget
							v-if="loyaltyCampaign"
							:campaign="loyaltyCampaign"
						/>
						<ReferralWidget
							v-if="referralCampaignProgress"
							:progress="referralCampaignProgress"
							:coupon="userReferralCoupon"
						/>
					</div>
				</div>
			</div>
		</div>
		<FloatingCartSummary ref="cartSummary" v-if="isMobile" />
		<UpcomingEventModal ref="upcomingEventModal" />
		<OngoingPopupEventModal ref="ongoingPopupEventModal" />
	</div>
</template>

<style scoped>
.residential-home >>> .desktop-center {
	max-width: 750px !important;
}

.home-container {
	padding: 0 20px;
}

.home-row {
	display: flex;
	flex-direction: column;
}

.home-row:first-child {
	margin-top: 40px;
}

.home-row + .home-row {
	margin-top: 55px;
}

.home-row > div {
	flex: 1;
}

.home-row h2 {
	font-weight: 600;
	font-size: 14px;
	color: var(--gR-base-80);
	margin-bottom: 20px;
}

.home-row h2 span {
	font-weight: 400;
	color: var(--sk-grey3);
}

.home-section {
	display: flex;
	flex-direction: column;
	min-height: 310px;
}

.residential-menus,
.event-details {
	display: flex;
}

.event-details {
	height: 100%;
	display: flex;
	flex-direction: column;
	cursor: pointer;
}

.event-placeholder {
	padding: 10px;
	text-align: center;
	height: 160px;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background: var(--sk-grey-bg);
	border-radius: 5px;
	border: 1px solid var(--gR-white2);
}

.event-placeholder div {
	font-size: 12px;
	color: var(--sk-grey3);
}

.event-placeholder .button {
	margin-top: 20px;
	font-size: 10px;
	font-weight: 400;
	padding: 5px 15px;
}

.event-placeholder .button i {
	font-size: 10px;
	margin-right: 10px;
}

.residential-menus > div {
	cursor: pointer;
	flex: 1;
}

.residential-menus > div + div {
	margin-left: 20px;
}

.residential-menus h2 {
	margin-bottom: 5px;
	max-width: 160px;
}

.residential-menu-image {
	margin-bottom: 15px;
	height: 160px;
	width: 100%;
	border-radius: 5px;
	background-position: center;
	background-repeat: no-repeat;
	background-size: cover;
	position: relative;
	border: 1px solid var(--gR-white2);
}

.residential-menu-image .pill {
	position: absolute;
	top: 15px;
	right: 15px;
}

.residential-menu-times {
	font-size: 12px;
	color: var(--sk-grey3);
	min-height: 18px;
}

.home-section-link {
	color: var(--gR-base-80);
	font-size: 12px;
	display: flex;
	justify-content: flex-end;
	flex-grow: 1;
	margin-top: 5px;
	padding-top: 15px;
}

.home-section-link i {
	margin-left: 20px;
	font-size: 10px;
	margin-bottom: 3.5px;
}

.upcoming-event-image {
	width: 100%;
	height: 160px;
	border-radius: 5px;
	position: relative;
	margin-bottom: 8px;
}

.upcoming-event-details {
	display: flex;
	align-items: center;
	margin-bottom: 34px;
}

.upcoming-event-details h2,
.residential-loyalty h2 {
	margin-bottom: 4px;
}

.upcoming-event-details .event-time {
	font-size: 12px;
	color: var(--sk-grey3);
}

.residential-loyalty .subtitle {
	color: var(--sk-grey2);
	font-size: 12px;
	margin-bottom: 20px;
}

.residential-loyalty-perks {
	display: grid;
	grid-template-columns: 1fr 1fr;
	row-gap: 20px;
	column-gap: 20px;
}

.event-cards {
	display: grid;
	grid-template-columns: 1fr 1fr;
	column-gap: 30px;
}

@media (max-width: 1000px) {
	.home-row {
		flex-direction: column;
	}

	.home-section + .home-section {
		margin-top: 25px;
	}
}

@media (max-width: 900px) {
	.residential-loyalty-perks {
		grid-template-columns: 1fr;
	}
}

@media (max-width: 600px) {
	.residential-menu-image {
		height: 100px;
	}

	.residential-menu-image .pill {
		top: 5px;
		right: 5px;
	}

	.event-cards {
		grid-template-columns: 1fr;
	}
}
</style>

<script>
import { UserNotificationType } from "@/enums/UserNotificationType"
import { eventBus } from "@/eventBus"
import { EventBusEvents } from "@/enums/EventBusEvents"
import { NotificationType } from "@/enums/NotificationType"
import { UniqueProductLabelId } from "@/enums/UniqueProductLabelId"
import moment from "moment"
import momentUtils from "@/moment-utils"
import eventDetailsMixin from "@/mixins/event-details-mixin"
import userPerksMixin from "@/mixins/user-perks-mixin"
import loaderMixin from "@/mixins/loader-mixin"
import mobileResponsivenessMixin from "@/mixins/mobile-responsiveness-mixin"
import ResidentialHomeNavbar from "@/components/navbars/ResidentialHomeNavbar.vue"
import ResidentialHomeBanner from "@/components/common/ResidentialHomeBanner.vue"
import ResidentialDaySelector from "@/components/common/ResidentialDaySelector.vue"
import LoyaltyWidget from "@/components/common/LoyaltyWidget.vue"
import ReferralWidget from "@/components/common/ReferralWidget.vue"
import ResidentialComingSoonSection from "@/components/common/ResidentialComingSoonSection.vue"
import FloatingCartSummary from "@/components/common/FloatingCartSummary.vue"
import UpcomingEventModal from "@/components/modals/UpcomingEventModal.vue"
import OngoingPopupEventModal from "@/components/modals/OngoingPopupEventModal.vue"
import { EventStatus } from "@reef-os/utils/enums/eventStatus"
import { BusinessEventType } from "@reef-os/utils/enums/businessEventType"
import EventCard from "@/components/events/EventCard.vue"

export default {
	name: "ResidentialHome",
	mixins: [
		eventDetailsMixin,
		userPerksMixin,
		loaderMixin,
		mobileResponsivenessMixin
	],
	components: {
		ResidentialHomeNavbar,
		ResidentialHomeBanner,
		ResidentialDaySelector,
		LoyaltyWidget,
		ReferralWidget,
		ResidentialComingSoonSection,
		FloatingCartSummary,
		UpcomingEventModal,
		OngoingPopupEventModal,
		EventCard
	},
	data: function () {
		return {
			mounted: false,
			mobileThresholdWidth: 900
		}
	},
	computed: {
		loaded() {
			return this.mounted && !this.loading
		},
		slug() {
			return this.$route.params.slug
		},
		showComingSoon() {
			return this.$store.state.business.is_active == 0
		},
		businessName() {
			return this.$store.state.business && this.$store.state.business.name
				? this.$store.state.business.name
				: ""
		},
		selectedDow() {
			if (this.$store.state.deliveryDate) {
				return momentUtils.normalizeDayOfWeek(
					moment(this.$store.state.deliveryDate.date, "YYYY-MM-DD")
				)
			}
			return null
		},
		menus() {
			if (
				!this.$store.state.deliveryDate ||
				!this.$store.getters.menusHash
			) {
				return []
			}
			const todaysDate = momentUtils.todaysDate()
			const selectedDate = moment(
				this.$store.state.deliveryDate.date,
				"YYYY-MM-DD"
			)
			const isToday = momentUtils.isSameDay(selectedDate, todaysDate)
			const offset =
				this.$store.state.business &&
				this.$store.state.business.timezone_offset_adjusted
					? this.$store.state.business.timezone_offset_adjusted
					: 0
			const now = moment.utc().add(offset, "m")

			const menu = {}
			this.$store.state.menu.forEach((item) => {
				if (!menu[item.menu_id]) {
					const inStock =
						this.$store.getters.menusHash[item.menu_id].menu
							.stock_status == 1
					const menuSchedule =
						this.$store.state.menuSchedules[item.menu_id]
					const selectedDowSchedules =
						menuSchedule && menuSchedule[this.selectedDow]
							? menuSchedule[this.selectedDow]
							: []

					if (isToday) {
						const shouldBeInStock = selectedDowSchedules.some(
							(schedule) =>
								momentUtils.isBetweenTimes(
									now,
									schedule.openTime,
									schedule.closeTime
								)
						)
						if (shouldBeInStock && !inStock) {
							return
						}
					}

					const itemMenu = this.$store.getters.menusHash[item.menu_id]
					menu[item.menu_id] = {
						id: item.menu_id,
						name: itemMenu.menu.name,
						itemImage: null,
						orderIndex: 0,
						nowServing: inStock && isToday
					}

					let orderIndex = null
					selectedDowSchedules.forEach((schedule) => {
						const numOpenTime = Number(
							schedule.openTime.replace(":", "")
						)
						if (orderIndex == null || orderIndex < numOpenTime) {
							orderIndex = numOpenTime
						}
					})
					let schedules = selectedDowSchedules
						? selectedDowSchedules
						: []
					menu[item.menu_id].orderIndex = orderIndex
					const timeSlots = momentUtils.generateTimeSlots(
						schedules,
						inStock,
						isToday
					)
					if (timeSlots.length > 1) {
						menu[item.menu_id].timeRange = `${
							timeSlots[0].text
						} - ${timeSlots[timeSlots.length - 1].text}`
					} else if (timeSlots.length > 0) {
						menu[item.menu_id].timeRange = `${timeSlots[0].text}`
					}
				}
				const isFeatured =
					item.labels &&
					item.labels
						.map((label) => label.id)
						.includes(UniqueProductLabelId.FEATURED)
				if (isFeatured && item.img && !menu[item.menu_id].itemImage) {
					const filenameArr = item.img.split(".")
					const sizedFileName =
						filenameArr[0] + "_" + "500x500." + filenameArr[1]
					menu[
						item.menu_id
					].itemImage = `${process.env.VUE_APP_BASE_RESTAURANTS_IMAGE_URL}${item.restaurant_slug}/${sizedFileName}`
				}
			})
			const menus = Object.values(menu).sort(
				(a, b) => a.orderIndex - b.orderIndex
			)
			return menus.splice(0, 3)
		},
		orderedUpcomingEvents() {
			let orderedUpcomingEvents = this.$store.state.businessEvents
				.filter((event) => {
					return event.isUpcoming
				})
				.sort((a, b) => {
					if (a.startTimestamp > b.startTimestamp) {
						return 1
					} else {
						return -1
					}
				})
			return orderedUpcomingEvents
		},
		event() {
			if (this.orderedUpcomingEvents.length > 0) {
				return this.orderedUpcomingEvents[0]
			}
			return null
		},
		nextEvents() {
			return this.orderedUpcomingEvents
				.filter((event) => event.id != this.event.id)
				.slice(0, 2)
		},
		isUserLoggedIn() {
			return this.$store.state.user && this.$store.state.user.id != 0
		},
		showPerks() {
			return this.isUserLoggedIn
		},
		showEventNotifyButton() {
			return (
				this.isUserLoggedIn &&
				(!this.$store.state.user.notifications ||
					!this.$store.state.user.notifications.find(
						(notification) =>
							notification.type ==
							UserNotificationType.UPCOMING_EVENT_SMS
					))
			)
		}
	},
	methods: {
		viewMenuClicked(menu) {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "View Active Menu",
				data: {
					menu: menu.id,
					dow: this.selectedDow
				}
			})
		},
		viewActiveMenuClicked() {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "View Active Menu",
				data: {
					dow: this.selectedDow
				}
			})
		},
		viewEventsClicked() {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "View Events Clicked",
				data: {
					content: "Residential Homepage"
				}
			})
		},
		eventClicked() {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Event Clicked",
				data: {
					content: "Residential Homepage"
				}
			})
		},
		eventReminderClicked() {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Event Remind Me Clicked"
			})
			this.toggleLoader(true)
			this.$store
				.dispatch("updateUserNotification", {
					type: UserNotificationType.UPCOMING_EVENT_SMS,
					optIn: true
				})
				.then(() => {
					eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
						message:
							"You will receive a notification when a new event is available",
						type: NotificationType.SUCCESS
					})
				})
				.catch(() => {
					eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
						message: "An error occurred, please try again.",
						type: NotificationType.ERROR
					})
				})
				.finally(() => {
					this.toggleLoader(false)
				})
		},
		displayActivationModal() {
			const isVerified = this.$store.state.user.isVerified != 0
			const isTokenDeeplink =
				this.$route.params.token != undefined ||
				this.$route.query.token != undefined

			const displayedActivationModal =
				this.$store.state.temporaryFlag.displayedActivationModal
			if (!displayedActivationModal && !isVerified && !isTokenDeeplink) {
				this.$store.dispatch("sendMixpanelEvent", {
					msg: "Activation Modal CTA Opened"
				})
				this.$store.dispatch("markFlag", {
					flag: "displayedActivationModal",
					value: true
				})
				eventBus.emit(EventBusEvents.OPEN_VERIFY_FLOW)
			}
		},
		displayUpcomingEventModal() {
			const displayedUpcomingModal =
				this.$store.state.temporaryFlag.displayedUpcomingModal
			const events = this.$store.state.businessEvents
				? this.$store.state.businessEvents
				: []
			if (this.$route.query.eventId) {
				const event = events.find(
					(event) =>
						event.id == this.$route.query.eventId &&
						event.isUpcoming
				)
				if (event) {
					this.openUpcomingEventModal(event)
					return true
				}
			} else if (!displayedUpcomingModal) {
				let upcomingEvent = null
				events
					.filter((event) => event.isUpcoming)
					.forEach((event) => {
						if (!upcomingEvent) {
							upcomingEvent = event
						} else if (
							upcomingEvent.status != EventStatus.ONGOING
						) {
							if (event.status == EventStatus.ONGOING) {
								upcomingEvent = event
							} else if (
								event.pre_order_end_date &&
								event.pre_order_end_time
							) {
								const eventTime = moment(
									`${event.pre_order_end_date} ${event.pre_order_end_time}`,
									"YYYY-MM-DD HH:mm:ss"
								)
								const upcomingEventTime = moment(
									`${upcomingEvent.pre_order_end_date} ${upcomingEvent.pre_order_end_time}`,
									"YYYY-MM-DD HH:mm:ss"
								)
								if (
									momentUtils.isBeforeDay(
										eventTime,
										upcomingEventTime
									)
								) {
									upcomingEvent = event
								}
							}
						}
					})
				if (upcomingEvent) {
					this.openUpcomingEventModal(upcomingEvent)
					return true
				}
			}
			return false
		},
		openUpcomingEventModal(event) {
			if (event.status == EventStatus.PREORDER) {
				this.$refs.upcomingEventModal.open({ eventId: event.id })
			} else if (
				event.status == EventStatus.ONGOING &&
				event.type == BusinessEventType.POPUP
			) {
				this.$refs.ongoingPopupEventModal.open({ eventId: event.id })
			}
		},
		trackComingSoon() {
			if (this.showComingSoon) {
				this.$store.dispatch("sendMixpanelEvent", {
					msg: "Coming Soon"
				})
			}
		},
		trackOutOfStock() {
			const menuStartDate = this.$store.getters.residentialMenuStartDate
			if (!moment().isSame(menuStartDate, "day")) {
				this.$store.dispatch("sendMixpanelEvent", {
					msg: "Out of Stock"
				})
				this.$store.dispatch(
					"sendForkLiftEvent",
					"app_order_out_of_stock"
				)
			}
		},
		openCartSummary() {
			if (this.isMobile && this.$store.getters.itemsInCartCount > 0) {
				this.$refs.cartSummary.open()
			} else {
				this.$router.push(`/${this.slug}/menu`)
			}
		}
	},
	mounted() {
		this.mounted = true
		this.getUserPerks()
		this.trackComingSoon()
		this.trackOutOfStock()
	},
	watch: {
		loaded: {
			immediate: true,
			handler(loaded) {
				if (!loaded) {
					return
				}
				if (!this.$route.query.exptType) {
					const displayedUpcomingEvent =
						this.displayUpcomingEventModal()
					if (!displayedUpcomingEvent) {
						const isVerified =
							this.$store.state.user.isVerified != 0
						const isTokenDeeplink =
							this.$route.params.token != undefined ||
							this.$route.query.token != undefined

						if (!isVerified && !isTokenDeeplink) {
							this.displayActivationModal()
						}
					}
				}
			}
		}
	}
}
</script>